import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { breadcrumbJsonLd, canonicalUrl, seo } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";

/*
 * The scorecard engine and its UI are the largest single feature on the site
 * and no other page needs them, so they load only when this route is opened.
 */
const Scorecard = React.lazy(() =>
  import("@/components/scorecard/Scorecard").then((m) => ({ default: m.Scorecard })),
);

const PATH = "/tools/ai-opportunity-scorecard";

const FAQS = [
  {
    q: "Is the AI readiness scorecard free?",
    a: "Yes. It is free, and there is no obligation to talk to us afterwards. You get the result whether or not you ever reply to us.",
  },
  {
    q: "How long does the AI assessment take?",
    a: "About four minutes. Twelve questions, multiple choice, no preparation and no documents needed.",
  },
  {
    q: "Do I need any technical knowledge?",
    a: "No. Every question is about how your business runs — who does what, how often, and where it slows down. Nothing asks about software architecture.",
  },
  {
    q: "What happens after I submit?",
    a: "You see your ranked map on screen straight away, and we email you a PDF copy. If you want to talk it through you can book a free call, and if you don't, you won't hear from us.",
  },
] as const;

export const Route = createFileRoute("/_site/tools/ai-opportunity-scorecard")({
  head: () =>
    seo({
      title: "Free Small Business AI Readiness Scorecard",
      description:
        "A free four-minute AI readiness assessment for small businesses. Twelve questions, six areas of your operations, a ranked map of where AI could pay.",
      path: PATH,
      jsonLd: [
        // No /tools level: that hub is a gated draft route and 404s on the live host.
        breadcrumbJsonLd([{ name: "AI Readiness Scorecard", path: PATH }]),
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${canonicalUrl(PATH)}#faqpage`,
          url: canonicalUrl(PATH),
          mainEntity: FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        },
      ],
    }),
  component: Page,
});

function Page() {
  return (
    <>
      <Section tone="paper" eager>
        <Container variant="narrow" className="py-0">
          <Eyebrow variant="teal">Free tool</Eyebrow>
          <h1 className="mt-4 max-w-[22ch] font-display text-display-lg text-ink-900 text-balance">
            Free AI readiness scorecard: see where AI could pay, in four minutes.
          </h1>
          <p className="mt-6 max-w-[60ch] text-body-lg text-ink-600">
            Twelve questions about how your business runs. No preparation, no documents, no cost.
          </p>
        </Container>

        <div className="mt-12">
          <React.Suspense fallback={<div className="min-h-[420px]" aria-hidden="true" />}>
            <Scorecard />
          </React.Suspense>
        </div>
      </Section>

      <Section tone="raised">
        <Container variant="narrow">
          <div className="space-y-12 text-body-lg text-ink-600 [&_a]:text-brand-700 [&_a]:underline [&_a]:underline-offset-4">
            <section>
              <h2 className="font-display text-h2 text-ink-900">What the scorecard measures</h2>
              <p className="mt-4 max-w-[68ch]">
                The twelve questions cover six areas of a small business where AI most often earns
                its place: customer communications, documents and data, sales and proposals,
                scheduling, knowledge, and finance and admin.
              </p>
              <p className="mt-4 max-w-[68ch]">
                Each area is scored on how much repetitive work sits there and how ready that work
                is to be handed over. A high score on one area and a low score everywhere else is a
                useful answer — it tells you where to spend and where to leave things alone.
              </p>
            </section>

            <section>
              <h2 className="font-display text-h2 text-ink-900">Who it is for</h2>
              <p className="mt-4 max-w-[68ch]">
                Owner-operated and small businesses working out whether AI is worth their time yet.
                If you are being pitched tools and have no way to judge which of them touches
                anything you care about, this is the shortest route to an opinion.
              </p>
              <p className="mt-4 max-w-[68ch]">
                It is not for a company that already has engineers building this. They are past the
                question the scorecard answers.
              </p>
            </section>

            <section>
              <h2 className="font-display text-h2 text-ink-900">What you get</h2>
              <p className="mt-4 max-w-[68ch]">
                A ranked map of your six areas, ordered by opportunity against effort, on screen
                immediately and emailed to you as a PDF. Where the answer is that AI is not worth
                pursuing yet, the result says so plainly rather than steering you toward a project.
              </p>
              <p className="mt-4 max-w-[68ch]">
                It is free and there is no follow-up obligation. If you want the longer version of
                the same question, with interviews and payback estimates, that is the{" "}
                <Link to="/services/ai-opportunity-assessment">AI opportunity assessment</Link>.
              </p>
              <p className="mt-6 text-body text-ink-500">
                We use your email to send the result and nothing else without asking. See the{" "}
                <Link to="/legal/privacy">privacy policy</Link>.
              </p>
            </section>
          </div>
        </Container>
      </Section>
    </>
  );
}
